import * as React from 'react';
import { useHistory } from 'react-router';
import {
  useGetAccountLazyQuery,
  useUpdateAccountMyPasswordMutation,
  useUpdateAccountPasswordMutation,
} from '../generated/graphql';
import { AppContext } from '../AppContext';
import { Loading } from '../Loading';
import { ToastContext } from '../components/Toast';
import { FormActions } from '../components/FormActions';
import { PageHeader, PageHeaderBreadcrumb } from '../components/PageHeader';
import { PasswordInput } from '../components/PasswordInput';
import { GraphQLErrorDialog } from '../components/Dialogs';
import { useValidation } from '../utils/Validation';
import { ValidationRules } from '../utils/ValidationRules';
import './AccountCreatePasswordPage.css';

interface CreateAccountPasswordPageProps {
  /** 'me'のときは自分のパスワード、数値のときはそのidのユーザーのパスワードを変える */
  target: number | 'me';
  /** キャンセルと変更の後に戻る画面のパス */
  backPath: string;
}

/** 対象のユーザーの名前が分かるときは見出しに名前を入れる */
function getPageTitle(targetName: string | undefined): string {
  if (targetName === undefined) {
    return 'パスワード変更';
  }
  return `${targetName}さんのパスワード変更`;
}

/** 自分のパスワードはマイページの下、他のユーザーのパスワードはユーザー一覧の下に置く */
function getBreadcrumbs(
  target: number | 'me',
  backPath: string,
  title: string
): PageHeaderBreadcrumb[] {
  if (target === 'me') {
    return [{ label: 'マイページ', to: backPath }, { label: title }];
  }
  return [
    { label: '管理画面' },
    { label: 'ユーザー一覧', to: backPath },
    { label: title },
  ];
}

export const AccountCreatePasswordPage: React.FC<CreateAccountPasswordPageProps> = function ({
  target,
  backPath,
}) {
  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const validation = useValidation({
    password: ValidationRules.Account.password,
  });
  const appContext = React.useContext(AppContext);
  const [password, setPassword] = React.useState<string>('');
  const [passwordMatched, setPasswordMatched] = React.useState(false);
  const [getAccount, getAccountResult] = useGetAccountLazyQuery();
  const [
    updatePassword,
    updatePasswordResult,
  ] = useUpdateAccountPasswordMutation({
    onCompleted,
    onError,
  });
  const [
    updateMyPassword,
    updateMyPasswordResult,
  ] = useUpdateAccountMyPasswordMutation({
    onCompleted,
    onError,
  });

  function onCompleted() {
    toastContext.showToast('パスワードを変更しました', 'success');
    history.push(backPath);
  }

  function onError() {
    toastContext.showToast('パスワードの変更に失敗しました', 'error');
  }

  // 管理者が他のユーザーのパスワードを変えるときは、対象のユーザーの名前を見出しに出すために取得する
  let targetName: string | undefined;
  if (target !== 'me') {
    if (!getAccountResult.called) {
      getAccount({ variables: { accountId: target } });
    }

    if (getAccountResult.error) {
      return <GraphQLErrorDialog error={getAccountResult.error} />;
    }

    if (getAccountResult.loading || !getAccountResult.data) {
      return <Loading />;
    }

    targetName = getAccountResult.data.getAccount.name;
  }

  const title = getPageTitle(targetName);
  const submitting = updatePasswordResult.loading || updateMyPasswordResult.loading;
  const submitDisabled = validation.isInvalid || !passwordMatched || submitting;

  const submit = function () {
    if (target === 'me') {
      if (!appContext.state.me) {
        return;
      }
      updateMyPassword({
        variables: {
          input: { account_id: appContext.state.me.id, password },
        },
      });
      return;
    }

    updatePassword({
      variables: {
        input: { account_id: target, password },
      },
    });
  };

  return (
    <div className="AccountCreatePasswordPage">
      <PageHeader
        title={title}
        breadcrumbs={getBreadcrumbs(target, backPath, title)}
      />
      <div className="AccountCreatePasswordPage-form">
        <PasswordInput
          validation={validation}
          onChange={(password, check) => {
            setPassword(password);
            setPasswordMatched(check);
          }}
        />
        <FormActions
          submitLabel="変更"
          submitDisabled={submitDisabled}
          onSubmit={submit}
          onCancel={() => history.push(backPath)}
        />
      </div>
    </div>
  );
};
